/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../theme';
import { 
  PlusCircle, 
  Trash2, 
  Edit, 
  Eye, 
  ShoppingBag,
  MoreVertical,
  Filter,
  Search,
  Image as ImageIcon
} from 'lucide-react';
import { ROUTES } from '../routes';
import { itemService } from '../services/itemService';

const ManageItems: React.FC = () => {
  const theme = useTheme();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchItems = async () => {
    try {
      const response = await itemService.getMyItems();
      if (response.status === 200) {
        setItems(response.data.data.map((item: any) => ({
          ...item,
          image: item.imageUrl
        })));
      } else {
        setError('Failed to fetch your items.');
      }
    } catch (err) {
      setError('An error occurred while loading items.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const response = await itemService.deleteItem(id);
      if (response.status === 200) {
        setItems(items.filter(item => item.id !== id));
      } else {
        alert('Failed to delete item.');
      }
    } catch (err) {
      alert('An error occurred.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: theme.colors.primary }}></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ color: theme.colors.text }}>My Listings</h1>
          <p style={{ color: theme.colors.mutedText }}>Manage your active and inactive items for sale.</p>
        </div>
        <Link
          to={ROUTES.ADD_LISTING}
          className="inline-flex items-center px-6 py-3 rounded-xl font-black text-white transition-all hover:scale-[1.02] shadow-lg shadow-blue-500/20"
          style={{ backgroundColor: theme.colors.primary }}
        >
          <PlusCircle size={20} className="mr-2" />
          Add New Listing
        </Link>
      </div>

      {error && (
        <div className="border px-4 py-3 rounded-xl" 
             style={{ backgroundColor: theme.colors.danger + '10', borderColor: theme.colors.danger, color: theme.colors.danger }}>
          {error}
        </div>
      )}

      {/* Filters/Actions Bar */}
      <div className="p-4 rounded-2xl border flex flex-col md:flex-row gap-4 items-center justify-between" 
           style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 opacity-30" size={18} />
          <input 
            type="text" 
            placeholder="Search your listings..." 
            className="w-full pl-10 pr-4 py-2 rounded-xl border focus:outline-none focus:ring-2 transition-all"
            style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
          />
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <button className="flex-1 md:flex-none inline-flex items-center justify-center px-4 py-2 border rounded-xl font-bold transition-all hover:opacity-80"
                  style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border, color: theme.colors.text }}>
            <Filter size={16} className="mr-2" />
            Filter
          </button>
          <button className="flex-1 md:flex-none inline-flex items-center justify-center px-4 py-2 border rounded-xl font-bold transition-all hover:opacity-80"
                  style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border, color: theme.colors.text }}>
            <MoreVertical size={16} />
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="p-16 rounded-3xl border border-dashed text-center space-y-4" 
             style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: theme.colors.primary + '10' }}>
            <ShoppingBag size={40} style={{ color: theme.colors.primary }} />
          </div>
          <div className="max-w-xs mx-auto">
            <h3 className="text-xl font-bold mb-1" style={{ color: theme.colors.text }}>No listings found</h3>
            <p className="mb-6" style={{ color: theme.colors.mutedText }}>You haven't listed any items for sale yet. Start earning today!</p>
            <Link to={ROUTES.ADD_LISTING} className="inline-flex items-center font-black" style={{ color: theme.colors.primary }}>
              Create your first listing <Eye size={16} className="ml-1" />
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {items.map((item) => (
            <div key={item.id} className="p-4 rounded-2xl border flex flex-col sm:flex-row items-center gap-4 transition-all hover:shadow-md"
                 style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
              {/* Image Preview */}
              <div className="w-full sm:w-24 h-24 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                {item.images && item.images[0] ? (
                  <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center opacity-20">
                    <ImageIcon size={32} />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-grow text-center sm:text-left">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
                  <h3 className="font-bold text-lg" style={{ color: theme.colors.text }}>{item.title}</h3>
                  <span className={`inline-flex px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider mx-auto sm:mx-0 ${
                    item.status === 'Active' ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'
                  }`}>
                    {item.status}
                  </span>
                </div>
                <div className="flex flex-wrap justify-center sm:justify-start items-center gap-x-4 gap-y-1 text-sm">
                  <span className="font-black" style={{ color: theme.colors.primary }}>${item.price.toFixed(2)}</span>
                  <span style={{ color: theme.colors.mutedText }}>ID: {item.id}</span>
                  <span style={{ color: theme.colors.mutedText }}>Created: {new Date(item.createdAt).toLocaleDateString()}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 w-full sm:w-auto justify-center">
                <Link to={`/item/${item.id}`} className="p-3 rounded-xl border hover:bg-gray-50 transition-colors"
                      style={{ borderColor: theme.colors.border, color: theme.colors.mutedText }} title="View">
                  <Eye size={18} />
                </Link>
                <Link to={`/dashboard/listings/${item.id}/edit`} className="p-3 rounded-xl border hover:bg-gray-50 transition-colors"
                      style={{ borderColor: theme.colors.border, color: theme.colors.mutedText }} title="Edit">
                  <Edit size={18} />
                </Link>
                <button onClick={() => handleDelete(item.id)} className="p-3 rounded-xl border hover:bg-red-50 transition-colors"
                        style={{ borderColor: theme.colors.border, color: theme.colors.danger }} title="Delete">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManageItems;
